import { DashboardMenuItem, PublicMenuItem } from '@common/types/common';
import { IUser, Roles } from '@common/types/user';
import { defineMessage } from 'react-intl';

import BlogIcon from '@assets/icons/navbar/blog.svg?react';
import DevelopersIcon from '@assets/icons/navbar/developers.svg?react';
import EventsIcon from '@assets/icons/navbar/events.svg?react';
import FirmwareIcon from '@assets/icons/navbar/firmware.svg?react';
import MediaAssetsIcon from '@assets/icons/navbar/media-assets.svg?react';
import NewsIcon from '@assets/icons/navbar/news.svg?react';
import OnlineTrainingIcon from '@assets/icons/navbar/online-training.svg?react';
import OverviewIcon from '@assets/icons/navbar/overview.svg?react';
import RepairFilesIcon from '@assets/icons/navbar/repair-files.svg?react';
import RLAFormIcon from '@assets/icons/navbar/rla-form.svg?react';
import SoftwareIntegratorsIcon from '@assets/icons/navbar/software-integrators.svg?react';
import VideosIcon from '@assets/icons/navbar/videos.svg?react';

//#region App Icons
import ClickAndDesignAppIcon from '@assets/icons/apps/app-click-and-design.svg?react';
import CommunityAppIcon from '@assets/icons/apps/app-community.svg?react';
import EOrderAppIcon from '@assets/icons/apps/app-eorder.svg?react';
import VictronOneIDAppIcon from '@assets/icons/apps/app-victron-oneid.svg?react';
import VictronWebsiteAppIcon from '@assets/icons/apps/app-victron-website.svg?react';
import VRMAppIcon from '@assets/icons/apps/app-vrm.svg?react';
//#endregion

export const getPublicItems = ({
    isLoggedIn,
    user,
}: {
    isLoggedIn: boolean;
    user?: Omit<IUser, 'full_name'>;
}): PublicMenuItem[] => {
    // Creating an exhaustive list of roles and permissions for readability and to better correlate with the reference table
    const isExact = !!user?.exact_debtor_number;
    const isEndUser = user?.role === Roles.END_USER;
    const isDistributor = user?.role === Roles.DISTRIBUTOR;
    const isDealer = user?.role === Roles.DEALER;
    const isSales = user?.role === Roles.SALES_DIRECTOR || user?.role === Roles.SALES_MANAGER;
    const isVictron = user?.role === Roles.VICTRON;
    const isInstaller = user?.role === Roles.INSTALLER || user?.role === Roles.AUTHORIZED_INSTALLER;

    return [
        {
            label: defineMessage({
                id: 'navbar.overview',
                defaultMessage: 'Overview',
            }),
            to: '',
            icon: OverviewIcon,
            disabled: !isLoggedIn,
        },
        {
            label: defineMessage({
                id: 'navbar.onlineTraining',
                defaultMessage: 'Online Training',
            }),
            to: 'online-training',
            icon: OnlineTrainingIcon,
            disabled: !isLoggedIn,
        },
        {
            label: defineMessage({
                id: 'navbar.events',
                defaultMessage: 'Events',
            }),
            to: 'events',
            icon: EventsIcon,
            disabled: !isLoggedIn,
        },
        {
            label: defineMessage({
                id: 'navbar.news',
                defaultMessage: 'News',
            }),
            to: 'news',
            icon: NewsIcon,
        },
        {
            label: defineMessage({
                id: 'navbar.blog',
                defaultMessage: 'Blog',
            }),
            to: 'blog',
            icon: BlogIcon,
            disabled: !isLoggedIn,
        },
        {
            label: defineMessage({
                id: 'navbar.videos',
                defaultMessage: 'Videos',
            }),
            to: 'videos',
            icon: VideosIcon,
            disabled: !isLoggedIn,
        },
        {
            label: defineMessage({
                id: 'navbar.softwareIntegrators',
                defaultMessage: 'Software Integrators',
            }),
            to: 'software-integrators-program',
            icon: SoftwareIntegratorsIcon,
        },
        {
            label: defineMessage({
                id: 'navbar.firmware',
                defaultMessage: 'Firmware',
            }),
            to: 'firmware',
            icon: FirmwareIcon,
        },
        {
            label: defineMessage({
                id: 'navbar.repairFiles',
                defaultMessage: 'Repair Files',
            }),
            to: 'repairs',
            icon: RepairFilesIcon,
            disabled: !isLoggedIn || !(isVictron || isExact),
        },
        {
            label: defineMessage({
                id: 'navbar.mediaAssets',
                defaultMessage: 'Media Assets',
            }),
            to: 'media-assets',
            icon: MediaAssetsIcon,
            disabled: !isLoggedIn || !(isDistributor || isDealer || isSales || isVictron || isExact),
        },
        {
            label: defineMessage({
                id: 'navbar.developers',
                defaultMessage: 'Developers',
            }),
            to: 'developers',
            icon: DevelopersIcon,
            disabled: !isLoggedIn || isEndUser,
        },
        {
            label: defineMessage({
                id: 'navbar.rlaForm',
                defaultMessage: 'RLA Form',
            }),
            to: 'rla-form',
            icon: RLAFormIcon,
            disabled: !isLoggedIn || !(isVictron || isSales || isDistributor || isDealer || isInstaller),
        },
    ];
};

const getDashboardItems: (isLoggedIn: boolean, user?: Omit<IUser, 'full_name'>) => DashboardMenuItem[] = (
    isLoggedIn,
    user
) => {
    const isSalesOrAdmin = user?.role && [Roles.SALES_DIRECTOR, Roles.SALES_MANAGER, Roles.VICTRON].includes(user.role);
    const isEventOrganizer = user?.is_event_organizer;

    const permissions = {
        overview: isSalesOrAdmin,
        members: isSalesOrAdmin,
        wtb: isSalesOrAdmin,
        news: isSalesOrAdmin,
        content_management: isSalesOrAdmin && user?.manages_banners,
        events: {
            base: isEventOrganizer || isSalesOrAdmin,
            all: isEventOrganizer || isSalesOrAdmin,
            add_video: isSalesOrAdmin,
            videos: isSalesOrAdmin,
        },
        training: isSalesOrAdmin,
        mailchimp: isSalesOrAdmin,
        dropbox: isSalesOrAdmin,
        action_log: isSalesOrAdmin,
        emails_list: isSalesOrAdmin,
        inbox_management: isSalesOrAdmin,
        support_requests: isSalesOrAdmin,
        checkTrainingIDEnabled: isSalesOrAdmin,
        lead_requests: isSalesOrAdmin,
    };

    return [
        {
            id: 'overview',
            label: 'Overview',
            icon: 'columns',
            link: { href: 'dashboard/' },
            disabled: !permissions.overview,
        },
        {
            id: 'members',
            label: 'Members',
            icon: 'user_check',
            link: { isReact: true, href: '/dashboard/members' },
            disabled: !permissions.members,
        },
        {
            id: 'wtb',
            label: 'Where to buy',
            icon: 'search_location',
            link: { href: 'dashboard/where-to-buy/' },
            disabled: !permissions.wtb,
        },
        {
            id: 'news',
            label: 'News',
            icon: 'newspaper',
            link: { href: 'dashboard/news/' },
            disabled: !permissions.news,
        },
        {
            id: 'content-management',
            label: 'Content management',
            icon: 'cards_stack',
            link: { isReact: true, href: '/dashboard/content-management/overview-banner/' },
            disabled: !permissions.content_management,
        },
        {
            id: 'events',
            label: 'Events',
            icon: 'chalkboard_teacher',
            link: { isReact: true, href: '/dashboard/events/' },
            disabled: !permissions.events.base,
            menuChildren: [
                {
                    id: 'events.all',
                    label: 'All events',
                    icon: 'calendar_alt_regular',
                    link: { isReact: true, href: '/dashboard/events/' },
                    disabled: !permissions.events.all,
                },
                {
                    id: 'events.add_video',
                    label: 'Add event video',
                    icon: 'play',
                    link: { href: 'dashboard/event-videos/add' },
                    disabled: !permissions.events.add_video,
                },
                {
                    id: 'events.videos',
                    label: 'Event videos',
                    icon: 'play_circle',
                    link: { href: 'dashboard/event-videos/' },
                    disabled: !permissions.events.videos,
                },
            ],
        },

        {
            id: 'training',
            label: 'Online Training',
            icon: 'graduation_cap',
            link: { href: 'dashboard/online-learning/' },
            disabled: !permissions.training,
        },

        {
            label: 'Check training ID',
            icon: 'certificate',
            link: {
                href: 'dashboard/check-training-id/',
            },
            disabled: !permissions.checkTrainingIDEnabled,
        },
        {
            label: 'MailChimp',
            icon: 'mailchimp',
            link: { href: 'dashboard/mailchimp/' },
            disabled: !permissions.mailchimp,
        },
        {
            label: 'Dropbox',
            icon: 'dropbox',
            link: { href: 'dashboard/dropbox/log/' },
            disabled: !permissions.dropbox,
        },
        {
            label: 'Action log',
            icon: 'scroll',
            link: { href: 'dashboard/log/' },
            disabled: !permissions.action_log,
        },
        {
            id: 'emails',
            label: 'Emails',
            icon: 'envelope',
            menuChildren: [
                {
                    label: 'Emails List',
                    icon: 'envelope_stack',
                    link: { href: '/dashboard/emails/' },
                    disabled: !permissions.emails_list,
                },
                {
                    label: 'Inbox Management',
                    icon: 'inbox_customize',
                    link: { isReact: true, href: '/dashboard/emails/inbox-management' },
                    disabled: !permissions.inbox_management,
                },
            ],
        },
        {
            label: 'Support Requests',
            icon: 'life_ring',
            link: { href: 'dashboard/support-requests/' },
            disabled: !permissions.support_requests,
        },
        {
            label: 'Lead Requests',
            icon: 'user_plus',
            link: { href: 'dashboard/lead-requests/' },
            disabled: !permissions.lead_requests,
        },
    ];
};

const DASHBOARD_FOOTER_ITEMS: DashboardMenuItem[] = [
    {
        label: 'Website',
        icon: 'columns',
        link: {
            href: '',
        },
    },
    {
        label: 'Logout',
        icon: 'sign_out',
        link: {
            href: 'accounts/logout/',
        },
    },
];

const PUBLIC_PADDING_CLASSNAME = 'lg:px-16 px-0 sm:py-11 p-2';

const APPS = [
    {
        label: 'Victron Website',
        icon: VictronWebsiteAppIcon,
        href: 'https://www.victronenergy.com/',
    },
    {
        label: 'Victron OneID',
        icon: VictronOneIDAppIcon,
        href: 'https://login.victronenergy.com/',
    },
    {
        label: 'E-Order',
        icon: EOrderAppIcon,
        href: 'https://eorder.victronenergy.com/',
    },
    {
        label: 'VRM',
        icon: VRMAppIcon,
        href: 'https://vrm.victronenergy.com/',
    },
    {
        label: 'Community',
        icon: CommunityAppIcon,
        href: 'https://community.victronenergy.com/',
    },
    {
        label: 'Click & Design',
        icon: ClickAndDesignAppIcon,
        href: 'https://click-and-design.victronenergy.com/',
    },
];

export { APPS, DASHBOARD_FOOTER_ITEMS, getDashboardItems, PUBLIC_PADDING_CLASSNAME };
